import React, { useRef, useCallback } from "react";

interface Props {
  children: React.ReactNode;
  className?: string;
}

/**
 * Wraps children in a horizontally scrollable container that supports
 * drag-to-scroll with the mouse (grab / grabbing cursor). On touch devices
 * native horizontal scrolling is used, which already behaves as drag & drop.
 * Clicks on children are suppressed when the user actually dragged.
 */
export default function DragScroll({ children, className = "" }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef({
    down: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    moved: false,
  });

  const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const el = ref.current;
    if (!el) return;
    drag.current.down = true;
    drag.current.moved = false;
    drag.current.startX = e.pageX;
    drag.current.startY = e.pageY;
    drag.current.scrollLeft = el.scrollLeft;
    el.classList.add("dragging");
    e.preventDefault();
  }, []);

  const onMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el || !drag.current.down) return;
    const dx = e.pageX - drag.current.startX;
    const dy = e.pageY - drag.current.startY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
      drag.current.moved = true;
    }
    el.scrollLeft = drag.current.scrollLeft - dx;
  }, []);

  const stopDrag = useCallback(() => {
    if (!drag.current.down) return;
    drag.current.down = false;
    ref.current?.classList.remove("dragging");
    // If the pointer was released outside the container no click event fires,
    // so clear the flag shortly after release to avoid swallowing the next click.
    // (A click, when it happens, is dispatched before this timeout runs.)
    window.setTimeout(() => {
      drag.current.moved = false;
    }, 0);
  }, []);

  const onClickCapture = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (drag.current.moved) {
      drag.current.moved = false;
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  return (
    <div
      ref={ref}
      className={`drag-scroll${className ? ` ${className}` : ""}`}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={stopDrag}
      onMouseLeave={stopDrag}
      onClickCapture={onClickCapture}
    >
      {children}
    </div>
  );
}
